import { Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { PlanCoverageRow } from "@audit-plan/shared";
import { Pill, RiskPill } from "../pills";
import { palette } from "../../theme";
import { ResponsiveList } from "../ResponsiveList";

const order = { high: 0, medium: 1, low: 2 } as const;

export function CoverageTable({ rows, canCreate, onPlan }: { rows: PlanCoverageRow[]; canCreate: boolean; onPlan: (supplierId: string) => void }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const data = rows
    .filter((r) => !r.phaseOut)
    .sort((a, b) => b.missing - a.missing || order[a.risk] - order[b.risk] || a.supplierName.localeCompare(b.supplierName));
  return (
    <ResponsiveList<PlanCoverageRow>
      size="small"
      rowKey="supplierId"
      pageSize={false}
      dataSource={data}
      emptyText={t("none")}
      tableProps={{ rowClassName: (r) => (r.missing ? "row-gap" : "") }}
      rowStyle={(r) => (r.missing ? { background: palette.badSoft } : undefined)}
      onCardClick={(r) => nav(`/suppliers/${r.supplierId}`)}
      card={{
        code: (r) => r.supplierCode,
        title: (r) => r.supplierName,
        tags: (r) => (
          <>
            <RiskPill r={r.risk} />
            {r.missing ? <Pill tone="bad">{`${t("gap")}: ${r.missing}`}</Pill> : <Pill tone="ok">OK</Pill>}
          </>
        ),
        fields: (r) => [
          [t("visitsRequired"), r.required],
          [t("visitsPlanned"), r.planned],
        ],
        footer: (r) =>
          r.missing && canCreate ? (
            <Button block icon={<PlusOutlined />} onClick={() => onPlan(r.supplierId)}>
              {t("a_plan")}
            </Button>
          ) : null,
      }}
      columns={[
        { title: t("sapCode"), dataIndex: "supplierCode", render: (v: string) => <span style={{ fontFamily: "var(--ap-mono)" }}>{v}</span> },
        { title: t("supplier"), render: (_: unknown, r: PlanCoverageRow) => <a onClick={() => nav(`/suppliers/${r.supplierId}`)}>{r.supplierName}</a> },
        { title: t("risk"), dataIndex: "risk", render: (v: string) => <RiskPill r={v} /> },
        { title: t("visitsRequired"), dataIndex: "required", align: "right" as const },
        { title: t("visitsPlanned"), dataIndex: "planned", align: "right" as const },
        {
          title: t("gap"),
          dataIndex: "missing",
          align: "right" as const,
          render: (v: number) => (v ? <b style={{ color: palette.bad }}>{v}</b> : <Pill tone="ok">OK</Pill>),
        },
        {
          title: "",
          align: "right" as const,
          render: (_: unknown, r: PlanCoverageRow) =>
            r.missing && canCreate ? (
              <Button size="small" icon={<PlusOutlined />} onClick={(e) => { e.stopPropagation(); onPlan(r.supplierId); }}>
                {t("a_plan")}
              </Button>
            ) : null,
        },
      ]}
    />
  );
}
